import { RouteState, SurveillanceRoute } from '../types/surveillanceRoute';

/**
 * Prevención P1 (plan 2026-10-15 § 5): horas tras el último tramo a partir de
 * las cuales un recorrido pausado se da por de OTRO turno.
 */
export const STALE_SHIFT_HOURS = 6;

const parse = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const sameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * ¿Este recorrido activo es de OTRO turno?
 *
 * Su.3827 y Su.3898 (prod, oct-2026): la noche dejó el recorrido PAUSADO en
 * «Fin de servicio»; el turno siguiente abrió el cronómetro, el único activo
 * era ese —con el mismo nombre que el suyo— y el cronómetro lo eligió solo.
 * Con esto el cronómetro pregunta antes de elegirlo.
 *
 * «Otro turno» (decisión del usuario, 15-oct) = creado OTRO DÍA en hora local
 * de la tablet, o, si está pausado, su último tramo acabó hace más de 6 h. Un
 * recorrido EN CURSO tiene un tramo abierto —la patrulla está rodando— y su
 * último tramo cerrado no dice nada.
 *
 * Solo lee lo que hay en caché (`creationDate`, `lastSegmentEndDate`), así que
 * funciona sin red. Sin fechas legibles no pregunta: mejor como hoy que
 * bloquear al operario por un dato que falta.
 */
export function isStaleShiftRoute(
  route: Pick<
    SurveillanceRoute,
    'state' | 'creationDate' | 'registerInitDate' | 'lastSegmentEndDate'
  >,
  now: Date = new Date()
): boolean {
  const created = parse(route.creationDate ?? route.registerInitDate);
  if (created && !sameLocalDay(created, now)) return true;

  if (route.state === RouteState.PAUSED) {
    const lastEnd = parse(route.lastSegmentEndDate);
    if (
      lastEnd &&
      now.getTime() - lastEnd.getTime() > STALE_SHIFT_HOURS * 3_600_000
    ) {
      return true;
    }
  }
  return false;
}

/** Lo que hace falta para decidir si se pregunta por un recorrido (P1). */
export interface ShiftQuestionContext {
  /** Recorridos por los que el operario ya contestó «Seguir en este». */
  continuedRouteIds: ReadonlySet<string>;
  /**
   * Revisión W5 (M1): ¿lo está grabando ESTA tablet (en curso y suyo)? Ese no
   * se pregunta nunca: la patrulla está rodando en él. Es la misma condición
   * que el `capturingRoute` del cronómetro.
   */
  isCapturedByThisDevice?: (route: SurveillanceRoute) => boolean;
  /**
   * QA T6 (banco, 15-oct): ¿podría ESTA tablet iniciar un tramo en él? (la
   * regla del botón «Iniciar tramo»: equipo, dueño del cronómetro y ningún
   * otro cronómetro suyo corriendo). Si no, no se pregunta: la pregunta no
   * lleva a nada y tras «Seguir» no hay botón.
   */
  canStartSegmentIn?: (route: SurveillanceRoute) => boolean;
  now: Date;
}

/**
 * ¿Hay que preguntar antes de usar este recorrido? Solo cuenta como contestado
 * un «Seguir en este» de verdad: una autoselección hecha cuando el recorrido
 * era reciente no es una respuesta.
 */
export function shouldAskAboutRoute(
  route: SurveillanceRoute,
  context: ShiftQuestionContext
): boolean {
  if (context.continuedRouteIds.has(route.id)) return false;
  if (context.isCapturedByThisDevice?.(route)) return false;
  if (context.canStartSegmentIn && !context.canStartSegmentIn(route))
    return false;
  return isStaleShiftRoute(route, context.now);
}

/**
 * Revisión W5 (I1): al ABRIR el cronómetro, ¿por qué recorrido hay que
 * preguntar? Por el que esté ELEGIDO —la tablet es del vehículo, comparte
 * cuenta entre turnos y la app se queda abierta: el recorrido de la noche,
 * elegido solo cuando era «de hoy», seguía elegido por la mañana (Su.3827)— o,
 * sin nada elegido, por el único activo (el que el cronómetro elegiría solo).
 *
 * Sin saber aún qué recorridos hay no se pregunta: «no sé» no es «es viejo».
 */
export function routeToAskAbout(
  input: ShiftQuestionContext & {
    routesKnown: boolean;
    activeRoutes: SurveillanceRoute[];
    selectedRouteId?: string;
  }
): SurveillanceRoute | null {
  if (!input.routesKnown) return null;
  const candidate = input.selectedRouteId
    ? input.activeRoutes.find((r) => r.id === input.selectedRouteId)
    : input.activeRoutes.length === 1
      ? input.activeRoutes[0]
      : undefined;
  if (!candidate) return null;
  return shouldAskAboutRoute(candidate, input) ? candidate : null;
}

/**
 * Revisión W5 (M2): la app tiene UNA sola ranura de modal y `openModal`
 * REEMPLAZA lo que haya (memoria `single_modal_slot_gotchas`). Si la pregunta
 * toca con otro modal abierto —un permiso bloqueante, la actualización
 * obligatoria—, no se pisa: queda pendiente (`askNow: false`) y quien llama no
 * abre el panel con el recorrido viejo; se pregunta cuando la ranura quede
 * libre o al siguiente intento de abrir el cronómetro.
 */
export function shiftQuestionDecision(
  input: Parameters<typeof routeToAskAbout>[0] & { modalSlotBusy: boolean }
): { route: SurveillanceRoute; askNow: boolean } | null {
  const route = routeToAskAbout(input);
  if (!route) return null;
  return { route, askNow: !input.modalSlotBusy };
}

/**
 * QA T6b (banco, 15-oct): ¿tiene esta cuenta un TRAMO ABIERTO en ese
 * recorrido? Es lo que mira el back para «cronómetro activo en otro
 * recorrido» (`assertNoOtherActiveTimer`, por la cuenta que operó el tramo).
 * El front miraba el estado `in-progress`, y un recorrido en curso SIN tramos
 * (un alta cuyo primer tramo falló) bloqueaba a la tablet en todos los demás.
 *
 * Sin el dato del servidor (`openSegmentUserIds` nulo: caché de antes de
 * actualizar o lectura fallida) se cae a la regla prudente de siempre —en
 * curso y con esta cuenta de dueña—: mejor no ofrecer un inicio que el back
 * rechazaría y que, encolado sin red, se descartaría al sincronizar.
 */
export function hasOwnOpenSegmentIn(
  route: Pick<SurveillanceRoute, 'state' | 'timerOwnerId' | 'openSegmentUserIds'>,
  userId: string | undefined | null
): boolean {
  if (!userId) return false;
  if (Array.isArray(route.openSegmentUserIds)) {
    return route.openSegmentUserIds.includes(userId);
  }
  return route.state === RouteState.IN_PROGRESS && route.timerOwnerId === userId;
}
